import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { PlayerLoyalty } from '@sorare/core/src/atoms/icons/PlayerLoyalty';
import { VaultBoost } from '@sorare/core/src/atoms/icons/VaultBoost';
import { iconInfoCircle } from '@sorare/core/src/atoms/icons/system/iconInfoCircle.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { HELP } from '@sorare/core/src/constants/__generated__/routes';

import type { RarityVaultHowItWorks_sealingConfig } from './__generated__/index.graphql.ts';

const YellowText = styled.strong`
  color: var(--c-yellow-400);
`;

const ParagraphWithIcon = styled(Horizontal)`
  gap: var(--double-unit);
  & > :first-child {
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(6 * var(--unit));
    height: calc(6 * var(--unit));
  }
  & > :last-child {
    flex: 1;
  }
`;

const StyledLink = styled.a`
  text-decoration: underline;
`;

const Warning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--intermediate-unit);

  & > :first-child {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--half-unit);
  }
  & > :last-child {
    flex: 1;
  }
`;

type Props = {
  sealingConfig?: RarityVaultHowItWorks_sealingConfig;
};

const Info = ({
  sealingConfig,
}: {
  sealingConfig: RarityVaultHowItWorks_sealingConfig;
}) => {
  if (sealingConfig.unsealCooldownDays === 0) {
    return (
      <FormattedMessage
        defaultMessage="Sealed cards cannot be <strong>bought, listed, sold, or used in lineups or missions</strong>. You can Unseal up to <strong>{unsealsPerPeriod, plural, one {# card} other {# cards}} every {unsealPeriodDays, plural, one {# day} other {# days}} per scarcity</strong>. Vaults are <strong>grouped by Scarcity</strong>."
        values={{
          strong: chunks => <YellowText>{chunks}</YellowText>,
          unsealsPerPeriod: sealingConfig?.unsealsPerPeriod,
          unsealPeriodDays: sealingConfig?.unsealPeriodDays,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      defaultMessage="Sealed cards cannot be <strong>bought, listed, sold, or used in lineups or missions</strong>. You can Unseal up to <strong>{unsealsPerPeriod, plural, one {# card} other {# cards}} every {unsealPeriodDays, plural, one {# day} other {# days}} per scarcity</strong>. After being sealed, <strong>each card has a {unsealCooldownDays}-day cooldown before it can be unsealed</strong>. Vaults are <strong>grouped by Scarcity</strong>."
      values={{
        strong: chunks => <YellowText>{chunks}</YellowText>,
        unsealsPerPeriod: sealingConfig?.unsealsPerPeriod,
        unsealPeriodDays: sealingConfig?.unsealPeriodDays,
        unsealCooldownDays: sealingConfig?.unsealCooldownDays,
      }}
    />
  );
};

export const RarityVaultHowItWorks = ({ sealingConfig }: Props) => {
  return (
    <>
      <Vertical gap={1.5}>
        <ParagraphWithIcon>
          <div>
            <VaultBoost size={4} />
          </div>
          <Vertical gap={0}>
            <BodyM bold color="var(--c-nd-900)">
              <FormattedMessage defaultMessage="Vaults" />
            </BodyM>
            <LabelS color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Collect and Seal multiple cards to level up your Vault." />
            </LabelS>
          </Vertical>
        </ParagraphWithIcon>
        <ParagraphWithIcon>
          <div>
            <Gift size={3} />
          </div>
          <Vertical gap={0}>
            <BodyM bold color="var(--c-nd-900)">
              <FormattedMessage defaultMessage="Unlock Perks" />
            </BodyM>
            <LabelS color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Reach milestones to earn Perks like boosted rewards" />
            </LabelS>
          </Vertical>
        </ParagraphWithIcon>
        <ParagraphWithIcon>
          <div>
            <PlayerLoyalty size={5} />
          </div>
          <Vertical gap={0}>
            <BodyM bold color="var(--c-nd-900)">
              <FormattedMessage defaultMessage="Card quality" />
            </BodyM>
            <LabelS color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Higher Quality Player Cards have higher sealing points" />
            </LabelS>
          </Vertical>
        </ParagraphWithIcon>
      </Vertical>
      <Warning>
        <LabelM color="var(--c-yellow-400)">
          <FontAwesomeIcon icon={iconInfoCircle} />
        </LabelM>
        <Vertical gap={1.5}>
          {!!sealingConfig && (
            <LabelM color="var(--c-nd-600)">
              <Info sealingConfig={sealingConfig} />
            </LabelM>
          )}
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage
              defaultMessage="Learn more in our <link>Help Center</link>."
              values={{
                link: chunks => (
                  <StyledLink href={HELP} target="_blank">
                    {chunks}
                  </StyledLink>
                ),
              }}
            />
          </LabelM>
        </Vertical>
      </Warning>
    </>
  );
};

RarityVaultHowItWorks.fragments = {
  sealingConfig: gql`
    fragment RarityVaultHowItWorks_sealingConfig on SealingConfig {
      unsealCooldownDays
      unsealPeriodDays
      unsealsPerPeriod
    }
  ` as TypedDocumentNode<RarityVaultHowItWorks_sealingConfig>,
};
